/**
 * "This was me" — whether THIS device still owes THIS member the pending-reset
 * notice (spec section 6.7, screen 7).
 *
 * The dismissal is per device by design: the notice exists so that a member
 * whose notes are about to be deleted sees it on whatever phone they pick up,
 * and one phone saying "yes, that was me" is not the others being told. There
 * is nowhere server-side to record it — the API has no per-device flag and this
 * feature adds none — so it lives where the enrolment banner's does:
 * `localStorage`, keyed by member id, which survives a relaunch and is not
 * shared between the members of a shared phone.
 *
 * WHAT IS STORED IS THE HOLD'S `requestedAt`, NOT A BOOLEAN. A boolean would
 * silence the notice for ever: a hold cancelled and then started again is a
 * SECOND reset, and the member has to be told about it as though the first had
 * never happened. Comparing the stored instant against the live one makes a new
 * hold a new notice, with nothing to expire and nothing to clean up.
 *
 * Nothing here is a secret — the value is a timestamp the API sent — so this
 * belongs in `localStorage` at all, for the same reason the banner key does.
 * And like that key it sits OUTSIDE the `next-steps-` logout sweep: a member who
 * signs out and back in on the same phone has not un-seen the notice.
 *
 * Every access is wrapped: `localStorage` throws outright in a locked-down
 * WebView and in some private-browsing modes, and the failure mode has to be
 * "ask again", never "fail to render" and never "assume dismissed".
 */

import { reconcileLocalKeys } from './reset';

/** One entry per member id, so two members on one phone each get their own. */
export const RESET_NOTICE_STORAGE_PREFIX = 'courses-e2ee-reset-notice:';

/**
 * @param {unknown} memberId
 * @returns {string | null} null when the member is not known yet
 */
export function resetNoticeStorageKey(memberId) {
  if (!Number.isInteger(memberId) || memberId <= 0) return null;
  return `${RESET_NOTICE_STORAGE_PREFIX}${memberId}`;
}

function read(key) {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function write(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // A device that cannot remember the dismissal asks again on the next
    // launch. Asking twice about a deletion is the right way round to fail.
  }
}

/** The instant that identifies one hold, or null when there is nothing to identify. */
function holdKey(pendingReset) {
  const at = pendingReset?.requestedAt;
  return typeof at === 'string' && at ? at : null;
}

/**
 * The member has seen this hold on this device and it was them.
 *
 * A hold with no `requestedAt` records nothing, so the notice comes back: an
 * unidentifiable hold dismissed by a wildcard would silence every later reset
 * as well.
 *
 * @param {number} memberId
 * @param {{ requestedAt?: string }|null} pendingReset
 */
export function dismissResetNotice(memberId, pendingReset) {
  const key = resetNoticeStorageKey(memberId);
  const hold = holdKey(pendingReset);
  if (!key || !hold) return;
  write(key, hold);
}

/**
 * @param {number} memberId
 * @param {{ requestedAt?: string }|null} pendingReset
 * @returns {boolean} true only for a hold this device has been told about
 */
export function isResetNoticeDismissed(memberId, pendingReset) {
  const key = resetNoticeStorageKey(memberId);
  const hold = holdKey(pendingReset);
  if (!key || !hold) return false;
  return read(key) === hold;
}

/**
 * The launch-time read that makes the notice possible at all (spec section 6.7,
 * screen 7: "on every approved device, AT APP LAUNCH RATHER THAN ON ENTERING
 * NEXT STEPS, because the member has to see it").
 *
 * WHY THIS RUNS AT LAUNCH FOR A MEMBER WHO NEVER OPENS NEXT STEPS. `pendingReset`
 * only exists on a `GET /api/me/encryption` snapshot, and until now the only
 * things that read it were Next Steps screens — so a notice hung off the Hub or
 * the chooser would reach exactly the member who was already looking. This is
 * installed from `App.vue`'s setup, next to the sealing hook and the
 * plaintext-cache guard, and watches the same signal App.vue's own
 * `courses-keys/hydrate` watcher feeds: the member id and whether this device
 * holds the key. Hydration is dispatched from the token and the profile id, with
 * no screen involved, so the key record comes back on a cold start and this read
 * follows it wherever in the app the member happens to be.
 *
 * WHY THE KEY IS THE TRIGGER, and not simply "we have a member". The notice is
 * for an APPROVED device, and holding MK is what an approved device is. Reading
 * the status for every signed-in member would put one request on every launch
 * of the app for the very large majority who have never enrolled and never
 * will; keying it on the local key record costs nothing for them, and a device
 * with no key can neither cancel a hold (`DELETE /reset` needs a signature) nor
 * be the "approved device" the spec is talking about.
 *
 * ONCE PER MEMBER, and the record is dropped the moment the key goes: on native
 * the document survives a sign-out, so a member signing back in on the same
 * phone re-hydrates into the same App.vue, and a latch that never cleared would
 * miss a hold started while they were signed out.
 *
 * AND IT IS ALSO WHERE A COMPLETED HOLD LANDS. The seven-day reset finishes on
 * the API's side lazily (decision 16), on whatever request happens to arrive
 * after it expires, so nothing tells this device: the read below simply comes
 * back with no bundle. Left at that, the member's key, their notes corpus and
 * every plaintext read-cache would survive on the approved phone the reset was
 * meant to clear — their "deleted" notes still readable there — and
 * `enrolIfEligible` would skip for ever on ALREADY_HAS_KEY, so Next Steps could
 * never set itself up again. So the read is acted on: `reconcileLocalKeys`
 * clears exactly what a tap on Start over here would have cleared.
 *
 * ONCE PER MEMBER THAT IT ACTS ON, and that latch is NOT the read's. A
 * reconciliation whose platform-copy removal failed puts the key straight back
 * on the next hydration, which moves the signal below, which asks again — for
 * ever, without a latch. The read's own latch cannot serve: it clears when the
 * key goes, because that is a sign-out. And it is keyed on having ACTED rather
 * than on having looked, so a member whose bundle was fine at launch and whose
 * hold completes while they sign out and back in is still reconciled.
 *
 * Never throws and never blocks: a failed read is recorded in
 * `courses-devices.readError` by the action itself, and the next launch asks
 * again.
 *
 * @param {{ store: object }} args
 * @returns {() => void} stops watching
 */
export function installResetNoticeCheck({ store }) {
  if (!store) throw new TypeError('installResetNoticeCheck needs the root Vuex store');

  // One derived value rather than a tuple: a watcher over an array rebuilds it
  // on every evaluation and so fires on changes that are not this one.
  const signal = () => {
    const memberId = store.getters['courses-keys/memberId'];
    const holdsKey = store.getters['courses-keys/hasMasterKey'] === true;
    return holdsKey && Number.isInteger(memberId) && memberId > 0 ? memberId : 0;
  };

  let asked = null;
  const reconciled = new Set();

  /**
   * The status this read landed with, acted on. Only ever the resolved value:
   * the getters keep the previous snapshot when a read fails, and "the request
   * did not land" must not be mistaken for "the bundle is gone".
   */
  const reconcile = async status => {
    if (!status) return;
    const member = store.getters['courses-keys/memberId'];
    if (!Number.isInteger(member) || member <= 0 || reconciled.has(member)) return;
    const { reconciled: acted, platformCopyNeutralised } = await reconcileLocalKeys({
      store,
      status,
    });
    if (!acted) return;
    // ⚠️ THE LATCH IS ON `acted`, NEVER ON THE NEUTRALISATION. Re-keying it on
    // the thing that is failing reopens the loop the paragraph above describes:
    // a failed overwrite puts the key back on the re-hydration, the signal moves
    // from the member id to 0 and back, the latch clears, and the member gets
    // one GET, one wipe and one re-hydration per turn for the rest of the
    // session. A within-session retry would have to count attempts and latch
    // after the second, which is not what this line is.
    reconciled.add(member);
    // The failure the member can be shown: this phone still holds a copy of a
    // key that opens nothing, and nothing here can shift it. The Devices screen
    // renders it (#1608); where it belongs is still an open design call.
    if (platformCopyNeutralised === false) {
      store.commit('courses-devices/setPlatformCopyStuck', true);
    }
  };

  const check = member => {
    if (!member) {
      // The key has gone (a sign-out, a wipe). Whoever comes back gets a read.
      asked = null;
      return;
    }
    if (asked === member) return;
    asked = member;
    store
      .dispatch('courses-devices/refreshStatus')
      .catch(error => {
        // Recorded in `courses-devices.readError` by the action itself, and the
        // next launch asks again. Resolving to null rather than rethrowing so
        // the reconciliation below is told there is nothing to act on: the
        // getters still hold the PREVIOUS snapshot.
        console.warn('[CoursesE2EE] The launch status read did not land:', error);
        return null;
      })
      .then(reconcile)
      .catch(error => {
        // An unhandled rejection here would be one in the app shell, at boot,
        // on every launch. The reset is the server's either way; what is left
        // undone is local, and the next launch tries again.
        console.warn('[CoursesE2EE] A completed reset was not reconciled here:', error);
      });
  };

  check(signal());
  return store.watch(signal, check);
}
